import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpContext } from '@angular/common/http';
import { Observable } from 'rxjs';
import { apiEndpoints } from '../../config/api.config';
import {
  AddressInModel,
  AddressOutModel,
  AddressUpdateModel,
  BillingAddressSelectionModel,
  CartResponseModel,
  CheckoutInModel,
  CheckoutResponseModel,
  CouponInModel,
  CustomerInModel,
  NfarmConfigurationInModel,
  PaymentMethodsResponseModel,
} from '../../models/ecommerce/cart.model';
import { SUPPRESS_HTTP_ERROR_TOAST } from '../../utils/http/http-context.tokens';

@Injectable({
    providedIn: 'root',
})
export class CartRepositoryService {
    private readonly http = inject(HttpClient);
    private readonly baseUrl = apiEndpoints.carts;

    public createCart(): Observable<CartResponseModel> {
        return this.http.post<CartResponseModel>(this.baseUrl, {});
    }

    public getCart(cartToken: string): Observable<CartResponseModel> {
        return this.http.get<CartResponseModel>(`${this.baseUrl}/${cartToken}`);
    }

    public setNfarmConfiguration(
        cartToken: string,
        payload: NfarmConfigurationInModel
    ): Observable<CartResponseModel> {
        return this.http.put<CartResponseModel>(
            `${this.baseUrl}/${cartToken}/nfarm-configuration`,
            payload
        );
    }

    /**
     * Questa chiamata crea anche l'account portale (`payload.account`), quindi i
     * suoi errori — email già registrata in primis — vengono mostrati inline dallo
     * step cliente: il toast globale sarebbe un doppione generico.
     */
    public setCustomer(cartToken: string, payload: CustomerInModel): Observable<CartResponseModel> {
        return this.http.put<CartResponseModel>(`${this.baseUrl}/${cartToken}/customer`, payload, {
            context: new HttpContext().set(SUPPRESS_HTTP_ERROR_TOAST, true),
        });
    }

    public listBillingAddresses(cartToken: string): Observable<AddressOutModel[]> {
        return this.http.get<AddressOutModel[]>(`${this.baseUrl}/${cartToken}/billing-addresses`);
    }

    public createBillingAddress(
        cartToken: string,
        payload: AddressInModel
    ): Observable<AddressOutModel> {
        return this.http.post<AddressOutModel>(`${this.baseUrl}/${cartToken}/billing-addresses`, payload);
    }

    public updateBillingAddress(
        cartToken: string,
        addressId: number,
        payload: AddressUpdateModel
    ): Observable<AddressOutModel> {
        return this.http.patch<AddressOutModel>(
            `${this.baseUrl}/${cartToken}/billing-addresses/${addressId}`,
            payload
        );
    }

    public deleteBillingAddress(cartToken: string, addressId: number): Observable<void> {
        return this.http.delete<void>(`${this.baseUrl}/${cartToken}/billing-addresses/${addressId}`);
    }

    public selectBillingAddress(
        cartToken: string,
        payload: BillingAddressSelectionModel
    ): Observable<CartResponseModel> {
        return this.http.put<CartResponseModel>(`${this.baseUrl}/${cartToken}/billing-address`, payload);
    }

    /**
     * Un codice rifiutato lascia il carrello com'era: l'errore lo mostra il
     * campo del coupon, non il toast globale.
     */
    public applyCoupon(cartToken: string, payload: CouponInModel): Observable<CartResponseModel> {
        return this.http.put<CartResponseModel>(`${this.baseUrl}/${cartToken}/coupon`, payload, {
            context: new HttpContext().set(SUPPRESS_HTTP_ERROR_TOAST, true),
        });
    }

    public removeCoupon(cartToken: string): Observable<CartResponseModel> {
        return this.http.delete<CartResponseModel>(`${this.baseUrl}/${cartToken}/coupon`, {
            context: new HttpContext().set(SUPPRESS_HTTP_ERROR_TOAST, true),
        });
    }

    public getPaymentMethods(cartToken: string): Observable<PaymentMethodsResponseModel> {
        return this.http.get<PaymentMethodsResponseModel>(
            `${this.baseUrl}/${cartToken}/payment-methods`
        );
    }

    public checkout(cartToken: string, payload: CheckoutInModel): Observable<CheckoutResponseModel> {
        return this.http.post<CheckoutResponseModel>(`${this.baseUrl}/${cartToken}/checkout`, payload);
    }
}
